import { takeEvery, all, put, call, takeLeading, delay } from 'redux-saga/effects'
import { PayloadAction } from '@reduxjs/toolkit'

import { ApiError, RequestStatus } from 'features/common'
import {
  accountRequest,
  renameAccountRequest,
  invitationRequest,
  deleteUserRequest,
  usersRequest,
  statsRequest,
  // account
  setAccountData,
  setAccountDataStatus,
  setAccountDataError,
  // invitation
  setInvitation,
  setAccountInvitationStatus,
  setAccountInvitationError,
  setDeleteUser,
  setUsers,
  // rename account
  setRenameAccountStatus,
  setRenameAccountError,
  // stats
  setStatsData,
  setStatsStatus,
  setStatsError,
  // join
  setRequiresJoinApprovalError,
  setRequiresJoinApprovalStatus,
  requiresJoinApprovalRequest,
  RequiresJoinApprovalAction,
  isDomainJoinableRequest,
  setIsDomainJoinableStatus,
  setIsDomainJoinableError,
  setIsDomainJoinable,
  // delete account
  deleteAccountRequest,
  setDeleteAccountStatus,
  setDeleteAccountError,
  InvitationAction,
  checkLdsQuotasStatsRequest,
  setLdsQuotasStatsStatus,
  setLdsQuotasStats,
  setLdsQuotasStatsError,
  DeleteUserAction
} from './accountSlice'

import {
  getUsers,
  deleteUser,
  postInvitation,
  editAccountName,
  getStats,
  editRequiresJoinApproval,
  getDomainJoinCheck,
  deleteAccount,
  getLdsQuotas
} from '../api/account'
import { Account, AccountStats, LdsQuotas } from '../types'
import { createApiError } from 'features/common/api/apiError'
import { isHardQuotaError } from 'features/quotas/utils'
import { getAtInstallation, installAnalyticsToolbox } from 'features/integrations/api/integrations'

import settings from 'settings'
import { AtInstallation, AtInstallationState } from 'features/integrations/types'
import { queryClient, queryClientUpdateCacheArrayElement } from 'queryClient'
import { atInstallationsKeys } from 'features/integrations/api/integrationsQueries'
import { setMyConnectionsStatus } from 'features/connections'
import { connectionsQueryKeys } from 'features/connections/api/connectionsQueries'
import { accountDataQuery } from '../api/accountQueries'
const { apiBaseUrl } = settings

function* fetchAccountData() {
  try {
    yield put(setAccountDataStatus(RequestStatus.Pending))
    const accountData: Account = yield queryClient.fetchQuery(accountDataQuery)

    yield put(setAccountData(accountData))
    yield put(setAccountDataStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setAccountDataStatus(RequestStatus.Rejected))
    yield put(setAccountDataError(e as ApiError))
  }
}

function* renameAccount(action: PayloadAction<string>) {
  try {
    yield put(setRenameAccountStatus(RequestStatus.Pending))
    yield call(editAccountName, action.payload)
    yield put(setRenameAccountStatus(RequestStatus.Fulfilled))
    yield put(setRenameAccountStatus(RequestStatus.Idle))

    yield put(setAccountDataStatus(RequestStatus.Idle))
  } catch (e) {
    yield put(setRenameAccountStatus(RequestStatus.Rejected))
    yield put(setRenameAccountError(e as ApiError))
  }
}

function* fetchUsers() {
  try {
    const users = yield call(getUsers)
    yield put(setUsers(users))
    // eslint-disable-next-line no-empty
  } catch {}
}

function* deleteUserFromAccount(action: PayloadAction<DeleteUserAction>) {
  try {
    const response = yield call(deleteUser, action.payload)
    yield put(setDeleteUser(response))
    yield put(setStatsStatus(RequestStatus.Idle))
  } catch (e) {
    // TODO
  }
}

function* sendInvitation(action: PayloadAction<InvitationAction>) {
  try {
    yield put(setAccountInvitationStatus(RequestStatus.Pending))
    const response = yield call(postInvitation, action.payload)
    yield put(setInvitation(response))
    yield put(setAccountInvitationStatus(RequestStatus.Fulfilled))
    yield put(setAccountInvitationStatus(RequestStatus.Idle))
  } catch (e) {
    yield put(setAccountInvitationStatus(RequestStatus.Rejected))
    const apiError = createApiError(e)
    yield put(setAccountInvitationError(apiError))
    if (isHardQuotaError(apiError)) {
      yield put(setStatsStatus(RequestStatus.Idle))
    }
  }
}

function* fetchStatsData() {
  try {
    yield put(setStatsStatus(RequestStatus.Pending))
    const statsData: AccountStats = yield call(getStats)
    yield put(setStatsData(statsData))
    yield put(setStatsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setStatsStatus(RequestStatus.Rejected))
    yield put(setStatsError(createApiError(e)))
  }
}

function* requiresJoinApproval(action: PayloadAction<RequiresJoinApprovalAction>) {
  try {
    yield put(setRequiresJoinApprovalStatus(RequestStatus.Pending))
    const account: Account = yield call(editRequiresJoinApproval, action.payload)
    yield put(setRequiresJoinApprovalStatus(RequestStatus.Fulfilled))
    yield put(setAccountData(account))
  } catch (e) {
    yield put(setRequiresJoinApprovalStatus(RequestStatus.Rejected))
    yield put(setRequiresJoinApprovalError(e as ApiError))
  }
}

function* isDomainJoinable(action: PayloadAction<string>) {
  try {
    yield put(setIsDomainJoinableStatus(RequestStatus.Pending))
    const status: boolean = yield call(getDomainJoinCheck, action.payload)
    yield put(setIsDomainJoinable(status))
    yield put(setIsDomainJoinableStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setIsDomainJoinableStatus(RequestStatus.Rejected))
    yield put(setIsDomainJoinableError(e as ApiError))
  }
}

function* deleteFullAccount(action: PayloadAction<string>) {
  try {
    yield put(setDeleteAccountStatus(RequestStatus.Pending))
    yield call(deleteAccount, action.payload)
    yield put(setDeleteAccountStatus(RequestStatus.Fulfilled))
    yield put(setDeleteAccountStatus(RequestStatus.Idle))

    yield put(setDeleteAccountStatus(RequestStatus.Idle))
  } catch (e) {
    yield put(setDeleteAccountStatus(RequestStatus.Rejected))
    yield put(setDeleteAccountError(e as ApiError))
  }
}

function* checkLdsQuotas() {
  try {
    yield put(setLdsQuotasStatsStatus(RequestStatus.Idle))
    const quotas: LdsQuotas[] = yield call(getLdsQuotas)
    yield put(setLdsQuotasStats(quotas))
    yield put(setLdsQuotasStatsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setLdsQuotasStatsStatus(RequestStatus.Rejected))
    yield put(setLdsQuotasStatsError(createApiError(e)))
    console.error(e)
  }
}

export function* atInstallationDoInstall({ id }: { id: string }) {
  let current: AtInstallation

  try {
    current = yield call(installAnalyticsToolbox, { id, apiBaseUrl })
    queryClientUpdateCacheArrayElement(atInstallationsKeys.all, current)

    while (true) {
      yield delay(2000)
      current = yield call(getAtInstallation, id)
      queryClientUpdateCacheArrayElement(atInstallationsKeys.all, current)

      switch (current.state) {
        case AtInstallationState.Installing:
          continue
        case AtInstallationState.Available:
          yield put(setMyConnectionsStatus(RequestStatus.Idle))
          queryClient.invalidateQueries(connectionsQueryKeys.all)
          return current
        case AtInstallationState.Failed:
          throw new Error(`At installation failed: ${current.output}`)
      }
    }
  } catch (error) {
    console.error('atInstallationPerform failed', error)
    throw error
  }
}

export function* watchAccount() {
  yield takeEvery(accountRequest.type, fetchAccountData)
  yield takeEvery(renameAccountRequest.type, renameAccount)
  yield takeEvery(usersRequest.type, fetchUsers)
  yield takeEvery(deleteUserRequest.type, deleteUserFromAccount)
  yield takeEvery(invitationRequest.type, sendInvitation)
  yield takeLeading(statsRequest.type, fetchStatsData)
  yield takeEvery(requiresJoinApprovalRequest.type, requiresJoinApproval)
  yield takeEvery(isDomainJoinableRequest.type, isDomainJoinable)
  yield takeEvery(deleteAccountRequest.type, deleteFullAccount)
  yield takeEvery(checkLdsQuotasStatsRequest.type, checkLdsQuotas)
}

export default function* accountSaga() {
  try {
    yield all([watchAccount()])
    throw new Error('Account saga')
  } catch (e) {
    console.error(e)
    // TODO
  }
}
